// MF-67 — Create/Edit a Journal Type: the top level of the scale (Type → Category → Severity →
// Template → Entry). Schools name and colour their own (Positive / Neutral / Negative by default). The code is
// set once on create — severities and recorded entries point at it — so renaming never breaks history.
// Position is set by drag-and-drop in the list (new types go last).
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { Box, Button, CircularProgress, Switch, TextField } from '@mui/material'
import { configPath } from '../paths'
import FormCard from '../components/FormCard'
import FormField from '../components/FormField'
import ColorField from '../components/ColorField'
import ColorBadge from '../components/ColorBadge'
import { inputSx, twoColumnSx } from '../components/formStyles'
import { DEFAULT_COLOR, isHex } from '../components/colorTokens'
import { getType, getTypes, createType, updateType } from '../../../../services/journal/types.service'

const slug = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

const TypeForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const [label, setLabel] = useState('')
  // (the code is derived from the label on create and never shown)
  const [description, setDescription] = useState('')
  const [usesSeverity, setUsesSeverity] = useState(false)
  const [color, setColor] = useState(DEFAULT_COLOR)
  const [colorOk, setColorOk] = useState(true)
  const [saving, setSaving] = useState(false)
  const [loadingData, setLoadingData] = useState(Boolean(id))

  useEffect(() => {
    if (!id) return
    getType(id)
      .then(({ data }) => {
        if (!data) return
        setLabel(data.label || '')
        setDescription(data.description || '')
        setUsesSeverity(Boolean(data.usesSeverity))
        setColor(isHex(data.color) ? data.color.toUpperCase() : DEFAULT_COLOR)
      })
      .catch(() => {})
      .finally(() => setLoadingData(false))
  }, [id])

  const onColorValid = useCallback((ok) => setColorOk(ok), [])
  const canSave = label.trim() && colorOk && !saving

  const onSave = async () => {
    setSaving(true)
    try {
      const payload = { label: label.trim(), description: description.trim(), color, usesSeverity }
      if (id) {
        await updateType(id, payload)
      } else {
        const { data } = await getTypes({ limit: 1 })
        await createType({ ...payload, code: slug(label), order: data?.totalRecords ?? 0 })
      }
      enqueueSnackbar('Type saved', { variant: 'success' })
      navigate(configPath('types'))
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error?.description || 'Could not save type', {
        variant: 'error'
      })
    } finally {
      setSaving(false)
    }
  }

  if (loadingData) {
    return (
      <div className='flex justify-center' style={{ padding: '40px 0' }}>
        <CircularProgress />
      </div>
    )
  }

  return (
    <Box
      className='grid items-start'
      style={{ gap: 24, padding: '24px 8px', maxWidth: 1040, margin: '0 auto' }}
      sx={twoColumnSx}
    >
      <FormCard
        title={id ? 'Type details' : 'New type'}
        description='Is the record good, neutral or a concern? Each category belongs to a type, and everything under it inherits it.'
        footer={
          <>
            <Button color='inherit' onClick={() => navigate(configPath('types'))}>
              Cancel
            </Button>
            <Button variant='contained' disabled={!canSave} onClick={onSave}>
              {saving ? 'Saving…' : 'Save type'}
            </Button>
          </>
        }
      >
        <FormField label='Name' required htmlFor='type-label' hint='e.g. Positive, Neutral, Negative'>
          <TextField
            id='type-label'
            size='small'
            fullWidth
            autoFocus={!id}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            sx={inputSx}
          />
        </FormField>
        <FormField
          label='Description'
          htmlFor='type-description'
          hint='Explain when to use it. Shown when staff pick a type.'
        >
          <TextField
            id='type-description'
            size='small'
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='e.g. Recognition, merits'
            sx={inputSx}
          />
        </FormField>
        <ColorField value={color} onChange={setColor} onValidChange={onColorValid} />

        <FormField
          label='Uses severity'
          help='Turn on for concerns (e.g. Negative). Templates in its categories then pick a severity (Minor → Critical); for other types severity is “not applicable”.'
          hint={
            usesSeverity
              ? 'Records of this type are graded Minor → Critical.'
              : 'Records of this type have no severity (e.g. merits, notes).'
          }
        >
          <label
            style={{ display: 'inline-flex', alignItems: 'center', fontSize: 13, color: 'rgb(51, 65, 85)' }}
          >
            <Switch checked={usesSeverity} onChange={(e) => setUsesSeverity(e.target.checked)} />
            {usesSeverity ? 'Yes' : 'No'}
          </label>
        </FormField>
      </FormCard>

      <FormCard title='Preview' style={{ position: 'sticky', top: 16 }}>
        <div
          className='flex flex-col items-center'
          style={{ gap: 10, padding: '28px 16px', borderRadius: 10, background: 'rgb(248, 250, 252)' }}
        >
          <ColorBadge label={label.trim() || 'Type name'} color={color} size='lg' />
          {description.trim() ? (
            <span style={{ fontSize: 13, color: 'rgb(71, 85, 105)', textAlign: 'center' }}>
              {description.trim()}
            </span>
          ) : null}
        </div>
      </FormCard>
    </Box>
  )
}

export default TypeForm
